import { FileSpreadsheet, Workflow, ShieldCheck, LayoutDashboard, AppWindow, Info, MessagesSquare, ArrowUpRight } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Parallax, DataCard, FloatPanel } from "./Decor";
import { Tilt } from "@/components/motion/Tilt";
import { formatPrice } from "@/lib/pricing";
import { useLang } from "@/i18n/LanguageContext";
import { trackClick } from "@/lib/track";
import { BUSINESS_PATHS, SERVICE_PATH_KEYS, inquireAboutPath } from "@/lib/businessPaths";

const ICONS = [FileSpreadsheet, Workflow, ShieldCheck, LayoutDashboard];
// Maps each service card (by order) to a key in the central pricing source.
const PRICE_KEYS = ["excelToSoftware", "aiWorkflows", "securityCompliance", "websiteDashboard"];

export const SpecialistSection = () => {
  const { t, lang } = useLang();
  const s = t.specialist;
  return (
    <section id="specialist" className="relative scroll-mt-24 py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-grid bg-grid-fade pointer-events-none" />
      {/* Ambient backlight + clean floating decor parked in the empty band beside
          the left-aligned heading (never over the cards, never clipped). */}
      <div className="glow-orb animate-glow pointer-events-none" style={{ width: 440, height: 440, top: -80, right: "5%", background: "var(--blue)", opacity: 0.16 }} />
      <Parallax className="hidden lg:block absolute z-0 right-[4%] top-[70px] pointer-events-none" from={20} to={-16}>
        <FloatPanel title="rebuild" className="animate-float-tilt" />
      </Parallax>
      <Parallax className="hidden xl:block absolute z-0 right-[18%] top-[150px] pointer-events-none" from={-16} to={18}>
        <DataCard label="rebuild" value="parity 100%" tone="green" className="animate-float-slow" />
      </Parallax>

      <div className="relative z-10 mx-auto max-w-7xl px-5">
        <SectionHeading eyebrow={s.eyebrow} title={s.title} intro={s.intro} testId="specialist-heading" />

        <div className="scene-3d mt-14 grid md:grid-cols-2 gap-5" data-testid="specialist-cards">
          {s.cards.map((c, i) => {
            const Icon = ICONS[i];
            const path = BUSINESS_PATHS.find((item) => item.key === SERVICE_PATH_KEYS[i]);
            return (
              <Tilt key={i} className="h-full">
                <article id={path?.serviceId} className="group glass-card depth-card sheen rounded-3xl p-7 relative overflow-hidden h-full flex flex-col scroll-mt-28" data-testid={`specialist-card-${i}`}>
                <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-[radial-gradient(circle,rgba(61,123,255,0.18),transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="flex items-center justify-between">
                  <span className="grid place-items-center h-12 w-12 rounded-2xl border border-[var(--line-strong)] bg-[rgba(61,123,255,0.08)] text-[var(--blue-bright)]">
                    <Icon size={22} />
                  </span>
                  {i === 0 ? (
                    <span className="font-mono text-[9px] uppercase text-[var(--cyan)]" data-testid="specialist-main-specialty">{s.specialtyTag}</span>
                  ) : c.optional ? (
                    <span className="font-mono text-[9px] uppercase tracking-widest text-[var(--green)] border border-[rgba(54,226,160,0.4)] rounded-full px-2.5 py-1" data-testid={`specialist-addon-${i}`}>
                      {s.addonTag}
                    </span>
                  ) : (
                    <span className="font-mono text-xs text-[var(--muted-2)]">0{i + 1}</span>
                  )}
                </div>
                <h3 className="mt-6 font-display text-xl font-semibold text-white" data-testid={`specialist-title-${i}`}>{c.title}</h3>
                <p className="mt-3 text-sm text-muted leading-relaxed" data-testid={`specialist-description-${i}`}>{c.desc}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {c.points.map((pt, pointIndex) => (
                    <span key={pt} data-testid={`specialist-point-${i}-${pointIndex}`} className="cell !text-[var(--blue-bright)] !border-[var(--line-strong)]">
                      {pt}
                    </span>
                  ))}
                </div>
                <div className="mt-6 flex items-center justify-between border-t border-[var(--line)] pt-4">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)]">{s.priceLabel}</span>
                  <span className="font-sans text-base font-bold tracking-normal text-[var(--cyan)]" data-testid={`specialist-price-${i}`}>{formatPrice(PRICE_KEYS[i], lang)}</span>
                </div>
                {path && (
                  <a href="#kontakt" onClick={() => inquireAboutPath(path.key)} className="mt-auto inline-flex items-center gap-2 pt-6 text-sm font-medium text-[var(--cyan)] transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--cyan)]" data-testid={`specialist-path-cta-${path.key}`}>
                    {c.cta} <ArrowUpRight size={16} aria-hidden="true" />
                  </a>
                )}
                </article>
              </Tilt>
            );
          })}
        </div>

        {/* Services-level CTA */}
        <div className="mt-8 flex justify-center">
          <a href="#kontakt" onClick={() => trackClick("contact")} className="btn-ghost" data-testid="specialist-cta">
            {s.cta} <ArrowUpRight size={16} />
          </a>
        </div>

        {/* Apps are one output, not the whole point */}
        <div className="mt-6 glass-card rounded-2xl p-6 flex items-start gap-4" data-testid="specialist-apps-note">
          <span className="grid place-items-center h-11 w-11 shrink-0 rounded-xl border border-[var(--line-strong)] bg-[rgba(61,123,255,0.08)] text-[var(--blue-bright)]">
            <AppWindow size={20} />
          </span>
          <p className="text-sm md:text-base text-white/90 leading-relaxed">{s.appsNote}</p>
        </div>

        {/* Pricing note (shared single source with the Pricing & Scope section) */}
        <div className="mt-4 glass-card rounded-2xl p-6 space-y-3" data-testid="specialist-pricing-note">
          <div className="flex items-start gap-3">
            <Info size={17} className="shrink-0 mt-0.5 text-[var(--blue-bright)]" />
            <p className="text-sm text-white/90 leading-relaxed">{t.pricing.noteMain}</p>
          </div>
          <div className="flex items-start gap-3">
            <MessagesSquare size={17} className="shrink-0 mt-0.5 text-[var(--green)]" />
            <p className="text-sm text-white/90 leading-relaxed">{t.pricing.noteScope}</p>
          </div>
        </div>
      </div>
    </section>
  );
};
